import { Alarma, type CondicionAlarma } from '../../domain/entities/Alarma';
import { Criticidad } from '../../domain/value-objects/Criticidad';
import { EstadoAlarma } from '../../domain/value-objects/EstadoAlarma';
import { aBooleano, aFecha, aNumero, aObjeto, aTexto } from './conversiones';

/** Fila de la vista `vw_alarmas_frontend`. */
export interface FilaAlarma extends Record<string, unknown> {
  id?: unknown;
  sensor_id?: unknown;
  sensor_nombre?: unknown;
  sensor_unidad_simbolo?: unknown;
  sensor_es_digital?: unknown;
  criticidad?: unknown;
  estado?: unknown;
  mensaje?: unknown;
  observaciones?: unknown;
  tipo_umbral?: unknown;
  valor_trigger?: unknown;
  estado_trigger?: unknown;
  valor_min?: unknown;
  valor_max?: unknown;
  estado_esperado?: unknown;
  /** JSON con la configuración del umbral en el momento del disparo. */
  umbral_info?: unknown;
  latched?: unknown;
  escalada?: unknown;
  nivel_escalacion?: unknown;
  /** `true` si el sensor sigue fuera de rango; `false` si ya volvió a normal. */
  runtime_en_fuera_rango?: unknown;
  timestamp_inicio?: unknown;
  timestamp_fin?: unknown;
  timestamp_reconocimiento?: unknown;
  reconocida_por_email?: unknown;
  nodo_id?: unknown;
  nodo_nombre?: unknown;
  area_id?: unknown;
  area_nombre?: unknown;
  cliente_id?: unknown;
  cliente_nombre?: unknown;
}

function aCondicion(valor: unknown): CondicionAlarma {
  const fueraDeRango = aBooleano(valor);
  if (fueraDeRango === true) return 'presente';
  if (fueraDeRango === false) return 'normalizada';
  return 'desconocida';
}

/**
 * Convierte una fila de `vw_alarmas_frontend` en entidad de dominio.
 *
 * Varios datos del umbral viven tanto en columnas propias como dentro del JSON
 * `umbral_info`, según la antigüedad de la alarma. Se prefiere la columna y se
 * recurre al JSON como respaldo, igual que hace la aplicación actual.
 */
export function aAlarma(fila: FilaAlarma): Alarma {
  const umbral = aObjeto(fila.umbral_info);

  return new Alarma({
    id: String(fila.id ?? ''),
    sensorId: aNumero(fila.sensor_id),
    sensorNombre: aTexto(fila.sensor_nombre),
    sensorUnidad: aTexto(fila.sensor_unidad_simbolo),
    esDigital: aBooleano(fila.sensor_es_digital) ?? fila.tipo_umbral === 'digital',
    criticidad: Criticidad.from(aTexto(fila.criticidad)),
    estado: EstadoAlarma.from(aTexto(fila.estado)),
    mensaje: aTexto(fila.mensaje),
    observaciones: aTexto(fila.observaciones),
    tipoUmbral: aTexto(fila.tipo_umbral),
    valorDisparo: aNumero(fila.valor_trigger),
    estadoDisparo: aBooleano(fila.estado_trigger),
    umbralMinimo: aNumero(fila.valor_min) ?? aNumero(umbral.valor_min),
    umbralMaximo: aNumero(fila.valor_max) ?? aNumero(umbral.valor_max),
    estadoEsperado: aBooleano(fila.estado_esperado) ?? aBooleano(umbral.estado_esperado),
    histeresis: aNumero(umbral.deadband),
    retenida: aBooleano(fila.latched) ?? aBooleano(umbral.latched) ?? false,
    condicion: aCondicion(fila.runtime_en_fuera_rango),
    escalada: aBooleano(fila.escalada) ?? false,
    nivelEscalacion: aNumero(fila.nivel_escalacion),
    fechaInicio: aFecha(fila.timestamp_inicio),
    fechaFin: aFecha(fila.timestamp_fin),
    fechaReconocimiento: aFecha(fila.timestamp_reconocimiento),
    reconocidaPor: aTexto(fila.reconocida_por_email),
    nodoId: aNumero(fila.nodo_id),
    nodoNombre: aTexto(fila.nodo_nombre),
    areaId: aNumero(fila.area_id),
    areaNombre: aTexto(fila.area_nombre),
    clienteId: aNumero(fila.cliente_id),
    clienteNombre: aTexto(fila.cliente_nombre),
  });
}
